import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import StreakBadge from "@/components/StreakBadge";
import { api } from "@/lib/api";
import { getCategoryImage, CATEGORY_FALLBACK_IMAGE } from "@/lib/categoryImages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import BidoraLogo from "@/components/BidoraLogo";
import PlusBadge from "@/components/PlusBadge";
import AdminAvatar from "@/components/AdminAvatar";
import SellerAvatar from "@/components/SellerAvatar";
import RecentApprovalsBanner from "@/components/RecentApprovalsBanner";
import {
  CaretDownIcon,
  DownloadSimpleIcon,
  GaugeIcon,
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  ShieldCheckIcon,
  SignOutIcon,
  SparkleIcon,
  UserCircleIcon,
  PencilSimpleIcon,
  CrownSimpleIcon,
  ChartLineUpIcon,
  BroadcastIcon,
  TrophyIcon,
  BellIcon,
  UsersThreeIcon,
  ChatTeardropDotsIcon,
} from "@phosphor-icons/react";

// Fallback list so the mobile category panel still works if /api/categories
// is slow or unreachable on first paint.
const FALLBACK_CATEGORIES = [
  "Sneakers",
  "Fragrances & Perfume",
  "Luxury Bags & Accessories",
  "Storage Unit Finds",
  "Trading Card Games",
  "Electronics",
  "Funko",
  "Rocks & Crystals",
  "Sports Cards",
  "Plants",
  "Women's Apparel",
  "Jewelry",
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [catPanelOpen, setCatPanelOpen] = useState(false);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  // Typeahead state for the global search — debounced lookup of users by
  // @username so people can find each other quickly from anywhere.
  const [searchOpen, setSearchOpen] = useState(false);
  const [userResults, setUserResults] = useState([]);
  const searchAbortRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    api
      .get("/categories")
      .then((r) => {
        const list = r?.data?.categories;
        if (Array.isArray(list) && list.length) setCategories(list);
      })
      .catch(() => {});
  }, []);

  // ---- Unread DMs badge ----
  // Polls /dm/unread every 30s as a cheap fallback, and subscribes to the
  // /ws/dm channel for instant updates when a new DM arrives. Both are
  // best-effort — never block the navbar render.
  const [unreadDM, setUnreadDM] = useState(0);
  useEffect(() => {
    if (!user) {
      setUnreadDM(0);
      return;
    }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/dm/unread");
        if (!cancelled) setUnreadDM(Number(data?.unread || 0));
      } catch {
        /* network — keep prior count */
      }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);

    let ws = null;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const url = new URL(base);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.pathname = "/api/ws/dm";
      ws = new WebSocket(url.toString());
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === "dm.hello" && typeof d.unread === "number") {
            setUnreadDM(d.unread);
          } else if (d.type === "dm.new") {
            // Recipient or sender — re-fetch authoritative count.
            fetchUnread();
          }
        } catch {
          /* non-JSON ping */
        }
      };
    } catch {
      /* WS unavailable — polling still keeps the badge fresh */
    }
    return () => {
      cancelled = true;
      clearInterval(t);
      if (ws) try { ws.close(); } catch { /* noop */ }
    };
  }, [user]);

  // Debounced user typeahead. Cancels the in-flight request when the
  // user keeps typing — crucial to avoid out-of-order results pinning
  // the dropdown to whatever resolved last.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setUserResults([]);
      return;
    }
    const t = setTimeout(async () => {
      if (searchAbortRef.current) searchAbortRef.current.abort();
      const ctrl = new AbortController();
      searchAbortRef.current = ctrl;
      try {
        const r = await api.get("/users/search", {
          params: { q: term, limit: 6 },
          signal: ctrl.signal,
        });
        setUserResults(r.data?.items || []);
      } catch {
        /* aborted or network — ignore */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    setSearchOpen(false);
    navigate(term ? `/browse?q=${encodeURIComponent(term)}` : "/browse");
  };

  const goToUser = (uid) => {
    setSearchOpen(false);
    setQ("");
    navigate(`/seller/${uid}`);
  };

  const pickCategory = (c) => {
    setCatPanelOpen(false);
    navigate(`/browse?category=${encodeURIComponent(c)}`);
  };

  return (
    <>
    <header
      className="sticky top-0 z-50 glass border-b border-zinc-200 safe-top safe-x"
      data-testid="main-navbar"
    >
      {/* Live "just verified / just approved" social-proof banner. Sits
          inside the sticky header so it stays pinned together with the
          nav row and mobile sub-nav as a single fixed unit — only the
          content below scrolls. */}
      <RecentApprovalsBanner />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 lg:gap-3">
        <Link to="/" className="group flex-shrink-0" data-testid="nav-logo">
          <BidoraLogo size={36} />
        </Link>

        {/* Search */}
        <form
          onSubmit={handleSearch}
          className="relative flex-1 min-w-[200px] max-w-[320px] xl:max-w-[420px] hidden md:block"
          data-testid="nav-search-form"
          onBlur={(e) => {
            // Close dropdown only if focus is leaving the whole form
            // (not just bouncing between input and a dropdown row).
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setTimeout(() => setSearchOpen(false), 120);
            }
          }}
        >
          <MagnifyingGlassIcon
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
          />
          <Input
            ref={searchInputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setSearchOpen(true)}
            placeholder="Search items or people…"
            className="pl-11 pr-4 h-11 text-base bg-white border-zinc-300 rounded-full focus-visible:ring-2 focus-visible:ring-[#1D78AB]/30 focus-visible:border-[#1D78AB] shadow-sm"
            data-testid="nav-search-input"
            aria-autocomplete="list"
            aria-expanded={searchOpen}
          />

          {/* Typeahead dropdown — items first, then user matches when present.
              Mounted only when there's a query to keep DOM lean. */}
          {searchOpen && q.trim().length >= 2 && (
            <div
              className="absolute left-0 right-0 top-full mt-2 rounded-xl border border-zinc-200 bg-white shadow-xl overflow-hidden z-50 min-w-[320px]"
              data-testid="nav-search-dropdown"
            >
              {/* People matches */}
              {userResults.length > 0 && (
                <>
                  <div className="px-3 py-2 text-[10px] font-mono tracking-[0.18em] uppercase text-zinc-500 bg-zinc-50">
                    People
                  </div>
                  {userResults.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => goToUser(u.user_id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 text-left transition-colors"
                      data-testid={`nav-search-user-${u.username}`}
                    >
                      <SellerAvatar
                        name={u.username}
                        picture={u.picture}
                        size={36}
                        sparkle={!!u.avatar_sparkle}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-zinc-950 truncate">
                          @{u.username}
                          {u.is_founder && (
                            <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold tracking-wider">
                              FOUNDER
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          {u.is_seller ? "Seller" : "Buyer"}
                          {u.verified && " · Verified"}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}

              {/* "Search all listings" CTA — always present so the user
                  can fall through to the full browse if they don't want
                  any of the people matches. */}
              <button
                type="submit"
                onMouseDown={(e) => e.preventDefault()}
                className="w-full flex items-center gap-2 px-3 py-3 hover:bg-[#1D78AB]/5 text-left text-sm text-[#1D78AB] font-semibold border-t border-zinc-100 transition-colors"
                data-testid="nav-search-submit-listings"
              >
                <MagnifyingGlassIcon size={14} />
                Search listings for "<span className="font-bold">{q.trim()}</span>"
              </button>
            </div>
          )}
        </form>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-5 text-sm flex-shrink-0">
          <Link to="/live" className="text-zinc-700 hover:text-[#1D78AB] font-medium" data-testid="nav-live">
            Live Now
          </Link>
          <Link to="/categories" className="text-zinc-700 hover:text-[#1D78AB] font-medium" data-testid="nav-categories">
            Categories
          </Link>
          <Link
            to="/demo"
            className="relative text-zinc-700 hover:text-[#1D78AB] font-medium hidden 2xl:flex items-center gap-1"
            data-testid="nav-demo"
          >
            <SparkleIcon size={13} weight="fill" className="text-amber-500" />
            Bidoria
            <span className="ml-0.5 text-[9px] uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 font-bold">
              new
            </span>
          </Link>
          <Link to="/prices" className="text-zinc-700 hover:text-[#1D78AB] font-medium hidden 2xl:block" data-testid="nav-prices">
            Prices
          </Link>
          <Link to="/plus" className="text-zinc-700 hover:text-[#1D78AB] font-medium flex items-center gap-1" data-testid="nav-plus">
            <CrownSimpleIcon size={14} weight="fill" className="text-amber-500" />
            Plus
          </Link>
          <Link to="/why-bidorah" className="text-zinc-700 hover:text-[#1D78AB] font-medium hidden 2xl:block" data-testid="nav-why-bidorah">
            Why us
          </Link>
          <Link
            to="/apply-to-sell"
            className="relative text-zinc-900 hover:text-[#1D78AB] font-semibold flex items-center gap-1.5"
            data-testid="nav-apply-to-sell"
          >
            <SparkleIcon size={14} weight="fill" className="text-amber-500" />
            Sell on Bidorah
            <span className="absolute -top-1.5 -right-3 h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          </Link>
        </nav>

        <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
          {user ? (
            <>
              <NotificationBell />
              <Link to="/feed" data-testid="nav-feed" className="hidden 2xl:block">
                <Button variant="ghost" className="px-2" aria-label="Feed" title="Feed">
                  <BroadcastIcon size={18} />
                </Button>
              </Link>
              <Link to="/messages" data-testid="nav-messages" className="hidden md:block relative">
                <Button variant="ghost" className="px-2" aria-label="Messages" title="Messages">
                  <ChatTeardropDotsIcon size={18} />
                </Button>
                {unreadDM > 0 && (
                  <span
                    className="absolute -top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-600 text-white text-[10px] font-extrabold tabular-nums leading-none"
                    data-testid="nav-messages-badge"
                  >
                    {unreadDM > 9 ? "9+" : unreadDM}
                  </span>
                )}
              </Link>
              <Link to="/orders" data-testid="nav-orders" className="hidden 2xl:block">
                <Button variant="ghost" className="px-2" aria-label="Orders" title="Orders">
                  <ShoppingBagIcon size={18} />
                </Button>
              </Link>
              <Link to="/refer" data-testid="nav-refer" className="hidden 2xl:block">
                <Button variant="ghost" className="px-2" aria-label="Refer" title="Refer">
                  <TrophyIcon size={18} />
                </Button>
              </Link>
              <Link to="/dashboard" data-testid="nav-dashboard" className="hidden 2xl:block">
                <Button variant="ghost" className="px-2" aria-label="Dashboard" title="Dashboard">
                  <GaugeIcon size={18} />
                </Button>
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="hidden md:block">
                  <Button variant="ghost" className="px-2" aria-label="Admin" title="Admin">
                    <ShieldCheckIcon size={18} />
                  </Button>
                </Link>
              )}
              {(user.role === "moderator" || user.role === "admin") && (
                <Link to="/mod" data-testid="nav-mod" className="hidden md:block">
                  <Button variant="ghost" className="px-2 text-[#1D78AB]" aria-label="Moderator" title="Moderator">
                    <ShieldCheckIcon size={18} weight="fill" />
                  </Button>
                </Link>
              )}
              {/* Streak badge is a 2xl-only flourish — at lg/xl it steals space
                  the Logout button needs. Always available in the avatar
                  dropdown via Dashboard for users on smaller screens. */}
              <span className="hidden 2xl:inline-flex"><StreakBadge user={user} /></span>
              {/* modal={false} → Radix won't lock body scroll while the menu
                  is open. Prevents a known mobile bug where tapping the
                  avatar after scrolling froze the page (route-change during
                  cleanup left `overflow:hidden` / `pointer-events:none` on
                  the <body>). The navbar menu doesn't need a modal scrim. */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="md:hidden gap-2 px-2"
                    aria-label="Account menu"
                    data-testid="nav-user-menu-mobile"
                  >
                    {user.picture ? (
                      user.avatar_sparkle ? (
                        <AdminAvatar src={user.picture} alt={user.name} size={28} />
                      ) : (
                        <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-full object-cover" />
                      )
                    ) : user.avatar_sparkle ? (
                      <AdminAvatar src={null} alt={user.name} size={28} />
                    ) : (
                      <UserCircleIcon size={22} className="text-zinc-600" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="nav-user-menu">
                  <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => navigate(`/seller/${user.user_id}`)}
                    data-testid="nav-my-profile"
                  >
                    <UserCircleIcon size={16} className="mr-2" /> View my profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/feed?tab=people")}>
                    <UsersThreeIcon size={16} className="mr-2" /> Following
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/messages")} data-testid="nav-messages-mobile">
                    <ChatTeardropDotsIcon size={16} className="mr-2" /> Messages
                    {unreadDM > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-600 text-white text-[10px] font-extrabold tabular-nums">
                        {unreadDM > 9 ? "9+" : unreadDM}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/settings/notifications")} data-testid="nav-notif-settings">
                    <BellIcon size={16} className="mr-2" /> Notifications
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/settings/profile")} data-testid="nav-edit-profile">
                    <PencilSimpleIcon size={16} className="mr-2" /> Edit profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/dashboard")}>
                    <GaugeIcon size={16} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => navigate("/dashboard?tab=library")}
                    data-testid="nav-my-downloads"
                  >
                    <DownloadSimpleIcon size={16} className="mr-2" /> My downloads
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onSelect={() => navigate("/admin")} data-testid="nav-admin-mobile-menu">
                      <ShieldCheckIcon size={16} className="mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  {(user.role === "moderator" || user.role === "admin") && (
                    <DropdownMenuItem onSelect={() => navigate("/mod")} data-testid="nav-mod-mobile-menu">
                      <ShieldCheckIcon size={16} weight="fill" className="mr-2 text-[#1D78AB]" /> Moderator
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* modal={false} → Radix won't lock body scroll while the menu
                  is open. Prevents a known mobile bug where tapping the
                  avatar after scrolling froze the page (route-change during
                  cleanup left `overflow:hidden` / `pointer-events:none` on
                  the <body>). The navbar menu doesn't need a modal scrim. */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="hidden md:flex gap-1.5 px-2"
                    aria-label="Account menu"
                    data-testid="nav-user-menu-desktop"
                  >
                    {user.picture ? (
                      user.avatar_sparkle ? (
                        <AdminAvatar src={user.picture} alt={user.name} size={24} />
                      ) : (
                        <img src={user.picture} alt={user.name} className="h-6 w-6 rounded-full object-cover" />
                      )
                    ) : user.avatar_sparkle ? (
                      <AdminAvatar src={null} alt={user.name} size={24} />
                    ) : (
                      <UserCircleIcon size={18} className="text-zinc-600" />
                    )}
                    <CaretDownIcon size={12} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => navigate(`/seller/${user.user_id}`)}
                    data-testid="nav-my-profile-desktop"
                  >
                    <UserCircleIcon size={16} className="mr-2" /> View my profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/feed?tab=people")} data-testid="nav-following-desktop">
                    <UsersThreeIcon size={16} className="mr-2" /> Following
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/messages")} data-testid="nav-messages-desktop">
                    <ChatTeardropDotsIcon size={16} className="mr-2" /> Messages
                    {unreadDM > 0 && (
                      <span className="ml-auto min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-600 text-white text-[10px] font-extrabold tabular-nums">
                        {unreadDM > 9 ? "9+" : unreadDM}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/orders")} data-testid="nav-orders-desktop">
                    <ShoppingBagIcon size={16} className="mr-2" /> Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/refer")} data-testid="nav-refer-desktop">
                    <TrophyIcon size={16} className="mr-2" /> Refer & earn
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/feed")} data-testid="nav-feed-desktop">
                    <BroadcastIcon size={16} className="mr-2" /> Feed
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => navigate("/settings/notifications")} data-testid="nav-notif-settings-desktop">
                    <BellIcon size={16} className="mr-2" /> Notifications
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/settings/profile")} data-testid="nav-edit-profile-desktop">
                    <PencilSimpleIcon size={16} className="mr-2" /> Edit profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => navigate("/dashboard")}>
                    <GaugeIcon size={16} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => navigate("/dashboard?tab=library")}
                    data-testid="nav-my-downloads-desktop"
                  >
                    <DownloadSimpleIcon size={16} className="mr-2" /> My downloads
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                className="gap-2 rounded-full px-2.5 h-9 sm:h-10 sm:px-3 flex-shrink-0"
                onClick={handleLogout}
                data-testid="nav-logout"
                aria-label="Logout"
                title="Logout"
              >
                <SignOutIcon size={16} /> <span className="hidden 2xl:inline">Logout</span>
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button
                  variant="ghost"
                  className="sm:hidden rounded-full border border-zinc-300 px-3 h-9 text-sm"
                >
                  Log in
                </Button>
                <Button variant="ghost" className="hidden sm:inline-flex">
                  Log in
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register" className="hidden sm:block">
                <Button className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full px-4">
                  Get started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile-only search row — the inline search bar is hidden below md
          to keep the main row clean, so we surface a full-width search bar
          here. Same state, same shortcut, same dropdown so users can find
          @people or listings from anywhere on the smallest screens too. */}
      <div className="md:hidden border-t border-zinc-100 bg-white/85 backdrop-blur-sm px-4 py-2">
        <form
          onSubmit={handleSearch}
          className="relative w-full"
          data-testid="nav-search-form-mobile"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setTimeout(() => setSearchOpen(false), 120);
            }
          }}
        >
          <MagnifyingGlassIcon
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
          />
          <Input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setSearchOpen(true)}
            placeholder="Search items or people…"
            className="pl-11 pr-10 h-11 text-base bg-white border-zinc-300 rounded-full focus-visible:ring-2 focus-visible:ring-[#1D78AB]/30 focus-visible:border-[#1D78AB] shadow-sm"
            data-testid="nav-search-input-mobile"
            aria-autocomplete="list"
            aria-expanded={searchOpen}
          />
          {q.length > 0 && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setQ("");
                setUserResults([]);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-full text-zinc-500 hover:text-[#1D78AB]"
              aria-label="Clear search"
              data-testid="nav-search-clear-mobile"
            >
              ✕
            </button>
          )}

          {searchOpen && q.trim().length >= 2 && (
            <div
              className="absolute left-0 right-0 top-full mt-2 rounded-xl border border-zinc-200 bg-white shadow-xl overflow-hidden z-50"
              data-testid="nav-search-dropdown-mobile"
            >
              {userResults.length > 0 && (
                <>
                  <div className="px-3 py-2 text-[10px] font-mono tracking-[0.18em] uppercase text-zinc-500 bg-zinc-50">
                    People
                  </div>
                  {userResults.map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => goToUser(u.user_id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 text-left transition-colors"
                      data-testid={`nav-search-user-mobile-${u.username}`}
                    >
                      <SellerAvatar
                        name={u.username}
                        picture={u.picture}
                        size={36}
                        sparkle={!!u.avatar_sparkle}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-zinc-950 truncate">
                          @{u.username}
                          {u.is_founder && (
                            <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[9px] font-bold tracking-wider">
                              FOUNDER
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          {u.is_seller ? "Seller" : "Buyer"}
                          {u.verified && " · Verified"}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}
              <button
                type="submit"
                onMouseDown={(e) => e.preventDefault()}
                className="w-full flex items-center gap-2 px-3 py-3 hover:bg-[#1D78AB]/5 text-left text-sm text-[#1D78AB] font-semibold border-t border-zinc-100 transition-colors"
                data-testid="nav-search-submit-listings-mobile"
              >
                <MagnifyingGlassIcon size={14} />
                Search listings for "<span className="font-bold">{q.trim()}</span>"
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Mobile-only sub-nav row: prominently surfaces Live + Categories + Sell.
          This gives Categories its own guaranteed real estate on every small
          screen (no more getting pushed off-right by a crowded main row). */}
      <div
        className="lg:hidden border-t border-zinc-100 bg-white/70 backdrop-blur-sm"
        data-testid="mobile-subnav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-10 flex items-center gap-1 text-xs font-semibold overflow-x-auto no-scrollbar">
          <Link
            to="/live"
            className="flex-shrink-0 text-zinc-700 hover:text-[#1D78AB] px-3 py-1 rounded-full"
            data-testid="nav-live-mobile"
            onClick={() => setCatPanelOpen(false)}
          >
            Live
          </Link>
          <button
            type="button"
            onClick={() => setCatPanelOpen((v) => !v)}
            aria-expanded={catPanelOpen}
            aria-controls="mobile-categories-panel"
            className={`flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-full transition-colors ${
              catPanelOpen
                ? "bg-[#1D78AB] text-white"
                : "text-zinc-700 hover:text-[#1D78AB]"
            }`}
            data-testid="nav-categories-mobile"
          >
            Categories
            <CaretDownIcon
              size={12}
              weight="bold"
              className={`transition-transform ${
                catPanelOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          <Link
            to="/plus"
            className="flex-shrink-0 inline-flex items-center gap-1 text-zinc-700 hover:text-[#1D78AB] px-3 py-1 rounded-full"
            data-testid="nav-plus-mobile"
            onClick={() => setCatPanelOpen(false)}
          >
            <CrownSimpleIcon size={11} weight="fill" className="text-amber-500" />
            Plus
          </Link>
          <Link
            to="/why-bidorah"
            className="flex-shrink-0 text-zinc-700 hover:text-[#1D78AB] px-3 py-1 rounded-full"
            data-testid="nav-why-bidorah-mobile"
            onClick={() => setCatPanelOpen(false)}
          >
            Why us
          </Link>
          {user && (
            <Link
              to="/feed"
              className="flex-shrink-0 inline-flex items-center gap-1 text-zinc-700 hover:text-[#1D78AB] px-3 py-1 rounded-full"
              data-testid="nav-feed-mobile"
              onClick={() => setCatPanelOpen(false)}
            >
              <BroadcastIcon size={11} />
              Feed
            </Link>
          )}
          <Link
            to="/prices"
            className="flex-shrink-0 text-zinc-700 hover:text-[#1D78AB] px-3 py-1 rounded-full"
            data-testid="nav-prices-mobile"
            onClick={() => setCatPanelOpen(false)}
          >
            Prices
          </Link>
          <Link
            to="/demo"
            className="flex-shrink-0 inline-flex items-center gap-1 text-zinc-900 hover:text-[#1D78AB] px-3 py-1 rounded-full font-semibold"
            data-testid="nav-demo-mobile"
            onClick={() => setCatPanelOpen(false)}
          >
            <SparkleIcon size={11} weight="fill" className="text-amber-500" />
            Bidoria
          </Link>
          {!user && (
            <Link
              to="/apply-to-sell"
              className="flex-shrink-0 relative inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-400 hover:bg-amber-300 text-zinc-900 font-bold"
              data-testid="nav-apply-to-sell-mobile"
              onClick={() => setCatPanelOpen(false)}
            >
              <SparkleIcon size={12} weight="fill" />
              Sell
              <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse ring-2 ring-white" />
            </Link>
          )}
        </div>
      </div>
    </header>

    {/* Mobile category panel — rendered OUTSIDE the sticky header so it
        flows in normal document order. The header above (logo + tabs row)
        stays pinned to the top of the viewport, and the user can scroll
        the panel content (and the rest of the page) naturally. */}
    {catPanelOpen && (
      <div
        id="mobile-categories-panel"
        className="lg:hidden border-b border-zinc-200 bg-white animate-in slide-in-from-top-1 duration-200"
        data-testid="mobile-categories-panel"
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="grid grid-cols-2 gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => pickCategory(c)}
                className="group relative overflow-hidden rounded-xl border border-zinc-200 bg-white hover:border-[#1D78AB] hover:shadow-md transition-all text-left"
                data-testid={`mobile-cat-${c.replace(/\s+/g, "-")}`}
              >
                <div className="aspect-[16/9] bg-zinc-100 overflow-hidden">
                  <img
                    src={getCategoryImage(c)}
                    alt={c}
                    loading="lazy"
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      if (e.currentTarget.src.indexOf(CATEGORY_FALLBACK_IMAGE) === -1) {
                        e.currentTarget.src = CATEGORY_FALLBACK_IMAGE;
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-2">
                  <p className="font-display font-bold text-[13px] text-white leading-tight drop-shadow truncate">
                    {c}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
